/**
 * PqTourCard — first-time explainer for the "Quantum-safe" chip.
 *
 * Mounts invisibly once the chip is on screen. If the user has
 * never seen the explainer, it pops a small absolute-positioned
 * card anchored below the chip, explains what the chip means, and
 * records the dismissal in localStorage so it never appears again.
 *
 * Wiped by the panic button thanks to the `ghostcomm_` prefix.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Atom, X } from 'lucide-react';

const SEEN_KEY = 'ghostcomm_pq_tour_seen';

export const PqTourCard = ({ visible }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!visible) return;
    try {
      if (localStorage.getItem(SEEN_KEY) === '1') return;
    } catch { return; }
    // Tiny delay so the chip is painted before the card points at it.
    const t = setTimeout(() => setOpen(true), 400);
    return () => clearTimeout(t);
  }, [visible]);

  // Click-outside → dismiss (and mark seen, one-shot).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) dismiss();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* noop */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      ref={ref}
      className="absolute left-0 top-full mt-2 z-40 w-80 rounded-lg border border-violet-500/40 bg-violet-950/80 backdrop-blur-md p-4 shadow-xl"
      data-testid="pq-tour-card"
      role="dialog"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-violet-300/60 hover:text-violet-200"
        data-testid="pq-tour-dismiss"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-center gap-2 text-violet-200 font-semibold text-sm mb-2">
        <Atom className="w-4 h-4" /> Quantum-safe, engaged.
      </div>
      <p className="text-xs text-violet-100/80 leading-relaxed">
        You're now on one of a handful of consumer messengers shipping
        post-quantum encryption (NIST FIPS 203 ML-KEM-768 + FIPS 204
        ML-DSA-65). Messages in this chat are wrapped in a hybrid
        envelope — a future quantum computer can't "harvest now,
        decrypt later." Tap the chip anytime to see the details.
      </p>
      <button
        onClick={dismiss}
        className="mt-3 text-[11px] text-violet-300 hover:text-violet-100 underline underline-offset-2"
        data-testid="pq-tour-ack"
      >
        Got it
      </button>
    </div>
  );
};

export default PqTourCard;
